'use memo'

import React from 'react'
import { ScrollSpyLayout } from 'src/components/ScrollSpyLayout'
import {
  FieldReadModeContext,
  useSectionEdit,
} from '../context/SectionEditContext'
import SectionEditActions from './SectionEditActions'

interface EditableSectionProps {
  id: string
  /** Already Title Cased by the caller. */
  title: string
  icon: React.ReactNode
  /**
   * Pinned to the right of the title. Only used with `renderLead`, which then
   * places the Edit / Save actions (otherwise they take this spot).
   */
  titleAccessory?: React.ReactNode
  /**
   * Content above the fields that also places the actions (Personal puts them
   * beside the photo). `actions` is null in a form without read mode.
   */
  renderLead?: (
    actions: React.ReactNode | null,
    isReadMode: boolean
  ) => React.ReactNode
  /**
   * False for a section with nothing to edit (registration-derived values
   * only): it always reads, and shows no Edit.
   */
  editable?: boolean
  children: React.ReactNode
}

/**
 * A player-form section that reads first when the form has a
 * `SectionEditContext` (Player Details' pattern): its fields show their values
 * until Edit, then the inputs with Cancel / Save. Without that context (Add
 * Player) it is a plain, always-editable section, exactly as before.
 */
const EditableSection: React.FC<EditableSectionProps> = ({
  id,
  title,
  icon,
  titleAccessory,
  renderLead,
  editable = true,
  children,
}) => {
  const sectionEdit = useSectionEdit()
  const isReadMode =
    !editable || (Boolean(sectionEdit) && sectionEdit?.editingSection !== id)
  const actions =
    sectionEdit && editable ? <SectionEditActions sectionKey={id} /> : null

  return (
    <ScrollSpyLayout.Section
      id={id}
      title={title}
      icon={icon}
      titleAccessory={renderLead ? titleAccessory : actions}
    >
      <FieldReadModeContext.Provider value={isReadMode}>
        {renderLead ? renderLead(actions, isReadMode) : null}
        {children}
      </FieldReadModeContext.Provider>
    </ScrollSpyLayout.Section>
  )
}

export default EditableSection
